import {
  type KeyboardEvent,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import CopyIcon from "./CopyIcon";
import CheckIcon from "./CheckIcon";
import ChevronIcon from "./ChevronIcon";
import type { Turn } from "../types/turn";
import type { Task } from "../types/task";
import { useTask } from "../context/TaskContext";
import {
  clearStoredTaskTurnDraft,
  loadStoredTaskTurnDraft,
  loadStoredTaskReasoningLevel,
  saveStoredTaskTurnDraft,
  saveStoredTaskReasoningLevel,
} from "../utils/createTaskStorage";
import { parseErrorResponse, runnerErrorMessage } from "../utils/errors";
import { useFeatures } from "../context/FeatureFlagContext";

const TERMINAL_STATUSES = new Set(["Done", "Failed"]);
const TURN_PROMPT_PREVIEW_MAX_HEIGHT_PX = 200;
const TASK_STATUS_MAX_LENGTH = 240;
const TASK_STATUS_ELIDE_SUFFIX = " ...";

// Marker prepended by UI for extended context models - strip when displaying.
// Handle various newline formats and multiple occurrences.
const EXTENDED_CONTEXT_MARKER_REGEX = /^\[\[EXTENDED_CONTEXT:1M\]\]\r?\n?/gm;

function stripExtendedContextMarker(prompt?: string): string {
  if (!prompt) return "";
  return prompt.replace(EXTENDED_CONTEXT_MARKER_REGEX, "");
}

const REASONING_LEVELS = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Max', label: 'Max' },
];

function formatTaskStatus(status?: string): string {
  if (!status) {
    return "";
  }
  if (status.length <= TASK_STATUS_MAX_LENGTH) {
    return status;
  }
  const elidedLength = TASK_STATUS_MAX_LENGTH - TASK_STATUS_ELIDE_SUFFIX.length;
  return status.slice(0, elidedLength) + TASK_STATUS_ELIDE_SUFFIX;
}

interface PromptPreviewRenderArgs {
  isExpanded: boolean;
  isOverflowing: boolean;
  toggle: () => void;
}

interface PromptPreviewProps {
  prompt?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: (args: PromptPreviewRenderArgs) => ReactNode;
}

function PromptPreview({
  prompt,
  isExpanded,
  onToggle,
  children,
}: PromptPreviewProps) {
  const preRef = useRef<HTMLPreElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOverflowStatus = useCallback(() => {
    const node = preRef.current;
    if (!node) {
      setIsOverflowing(false);
      return;
    }
    const shouldOverflow = node.scrollHeight > TURN_PROMPT_PREVIEW_MAX_HEIGHT_PX;
    setIsOverflowing((prev) => (prev === shouldOverflow ? prev : shouldOverflow));
  }, []);

  useEffect(() => {
    updateOverflowStatus();
  }, [prompt, isExpanded, updateOverflowStatus]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    function handleResize() {
      updateOverflowStatus();
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateOverflowStatus]);

  return (
    <>
      <pre
        ref={preRef}
        style={{
          background: "#d1dff6",
          padding: "8px",
          borderRadius: "4px",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          maxHeight: isExpanded ? "none" : `${TURN_PROMPT_PREVIEW_MAX_HEIGHT_PX}px`,
          overflowY: isExpanded ? "visible" : "hidden",
        }}
      >
        {stripExtendedContextMarker(prompt)}
      </pre>
      {children?.({ isExpanded, isOverflowing, toggle: onToggle })}
    </>
  );
}

interface HasTurnID {
  TurnID?: string;
}

function parseTurnNumber(turnID?: string): number {
  if (!turnID) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = Number.parseInt(turnID, 10);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function compareTurnsAscending(a: HasTurnID, b: HasTurnID): number {
  const aValue = parseTurnNumber(a?.TurnID);
  const bValue = parseTurnNumber(b?.TurnID);
  if (aValue !== bValue) {
    return aValue - bValue;
  }
  const aId = a?.TurnID ?? "";
  const bId = b?.TurnID ?? "";
  return aId.localeCompare(bId);
}

function compareTurnsDescending(a: HasTurnID, b: HasTurnID): number {
  return compareTurnsAscending(b, a);
}

interface TaskHistoryViewProps {
  taskID?: string;
  workstreamID?: string;
  selectedTurnID?: string;
  onSelectTurn?: (
    turnID?: string,
    options?: { userInitiated?: boolean }
  ) => void;
}

export default function TaskHistoryView({
  taskID,
  workstreamID,
  selectedTurnID,
  onSelectTurn,
}: TaskHistoryViewProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [copiedTurn, setCopiedTurn] = useState<string | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const copyTimeout = useRef<number | null>(null);
  const [taskDetails, setTaskDetails] = useState<Task | null>(null);
  const [newPrompt, setNewPrompt] = useState("");
  const [newReasoningLevel, setNewReasoningLevel] = useState("");
  const [reasoningLevelInitialized, setReasoningLevelInitialized] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { status: taskStatus, turnID: taskTurnID } = useTask();
  const features = useFeatures();
  const autoSelectionTargetRef = useRef<string | undefined>(undefined);
  const initialSelectionMadeRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const previousLatestTurnIDRef = useRef<string | undefined>(undefined);
  const reloadSequenceRef = useRef(0);
  const [expandedPrompts, setExpandedPrompts] = useState<Record<string, boolean>>({});
  const wsQuery = workstreamID ? `?workstreamID=${encodeURIComponent(workstreamID)}` : '';
  const togglePromptExpansion = useCallback((key: string) => {
    setExpandedPrompts((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? false),
    }));
  }, []);

  const reloadTurns = useCallback(
    async (signal?: AbortSignal) => {
      const requestId = reloadSequenceRef.current + 1;
      reloadSequenceRef.current = requestId;
      if (!taskID) {
        setTurns([]);
        return;
      }
      try {
        const resp = await fetch(
          `/api/tasks/${taskID}/turns${wsQuery}`,
          signal ? { signal } : undefined,
        );
        if (!resp.ok) {
          return;
        }
        const data = await resp.json();
        if (!signal?.aborted && requestId === reloadSequenceRef.current) {
          const turnsList: unknown[] = Array.isArray(data.Turns)
            ? data.Turns
            : [];
          const normalizedTurns = turnsList.filter((turn): turn is Turn => {
            if (!turn || typeof turn !== "object") {
              return false;
            }
            const candidate = turn as Partial<Turn>;
            return typeof candidate.TurnID === "string";
          });
          const sortedTurns = [...normalizedTurns].sort(compareTurnsDescending);
          setTurns(sortedTurns);
        }
      } catch (err) {
        if (!signal?.aborted && requestId === reloadSequenceRef.current) {
          console.error("ListTurns failed", err);
        }
      }
    },
    [taskID, wsQuery],
  );

  useEffect(() => {
    if (!taskID) {
      setTaskDetails(null);
      setNewPrompt("");
      setLoadError(null);
      setSubmitError(null);
      setReasoningLevelInitialized(false);
      return;
    }

    const abort = new AbortController();
    let cancelled = false;

    setLoadError(null);
    setSubmitError(null);
    setReasoningLevelInitialized(false);
    const storedDraft = loadStoredTaskTurnDraft(taskID);
    setNewPrompt((prev) => (prev === storedDraft ? prev : storedDraft));

    async function loadTaskDetails() {
      try {
        const resp = await fetch(`/api/tasks/${taskID}${wsQuery}`, { signal: abort.signal });
        if (!resp.ok) {
          const msg = await resp.text();
          if (!cancelled) {
            setLoadError(msg ? `Failed to load task details: ${msg}` : "Failed to load task details.");
            setTaskDetails(null);
          }
          return;
        }
        const data: Task = await resp.json();
        if (!cancelled) {
          setTaskDetails(data);
        }
      } catch (err) {
        if (!abort.signal.aborted) {
          console.error("GetTask failed", err);
          if (!cancelled) {
            setLoadError("Failed to load task details.");
            setTaskDetails(null);
          }
        }
      }
    }

    loadTaskDetails();
    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [taskID, wsQuery]);

  // Default turn reasoning level from localStorage, task, or last turn
  useEffect(() => {
    if (!taskID || reasoningLevelInitialized) {
      return;
    }
    // First check localStorage for a saved reasoning level for this task
    const storedLevel = loadStoredTaskReasoningLevel(taskID);
    if (storedLevel && REASONING_LEVELS.some(r => r.value === storedLevel)) {
      setNewReasoningLevel(storedLevel);
      setReasoningLevelInitialized(true);
      return;
    }
    // If task has reasoning level, use it
    if (taskDetails?.ReasoningLevel) {
      const isValidLevel = REASONING_LEVELS.some(r => r.value === taskDetails.ReasoningLevel);
      const level = isValidLevel ? taskDetails.ReasoningLevel : 'Medium';
      setNewReasoningLevel(level);
      saveStoredTaskReasoningLevel(taskID, level);
      setReasoningLevelInitialized(true);
      return;
    }
    // Otherwise wait for turns to load and use the most recent turn's reasoning level
    if (turns.length > 0) {
      const lastTurnReasoningLevel = turns[0]?.ReasoningLevel;
      const reasoningLevel = lastTurnReasoningLevel || 'Medium';
      const isValidLevel = REASONING_LEVELS.some(r => r.value === reasoningLevel);
      const level = isValidLevel ? reasoningLevel : 'Medium';
      setNewReasoningLevel(level);
      saveStoredTaskReasoningLevel(taskID, level);
      setReasoningLevelInitialized(true);
    }
  }, [taskID, taskDetails, reasoningLevelInitialized, turns]);

  useEffect(() => {
    if (!taskID) {
      setTurns([]);
      return;
    }
    const abort = new AbortController();
    setTurns([]);
    reloadTurns(abort.signal);
    return () => {
      abort.abort();
    };
  }, [taskID, reloadTurns]);

  useEffect(() => {
    if (!taskID || !taskTurnID) {
      return;
    }
    const abort = new AbortController();
    reloadTurns(abort.signal);
    return () => {
      abort.abort();
    };
  }, [taskID, taskTurnID, reloadTurns]);

  const latestTurnID = turns.length > 0 ? turns[0].TurnID : undefined;
  const newestTurn = turns.length > 0 ? turns[0] : undefined;
  const newestTurnID = newestTurn?.TurnID;
  const newestTurnStatus = newestTurn?.Status;
  const newestTurnOutputMessage = newestTurn?.OutputMessage;
  const newestTurnErrorMessage = newestTurn?.ErrorMessage;
  const orderedTurns = useMemo(() => {
    return [...turns].sort(compareTurnsAscending);
  }, [turns]);

  useEffect(() => {
    setExpandedPrompts({});
  }, [taskID]);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    window.requestAnimationFrame(() => {
      const node = scrollContainerRef.current;
      if (!node) {
        return;
      }
      node.scrollTop = node.scrollHeight;
    });
  }, []);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceFromBottom =
        target.scrollHeight - (target.scrollTop + target.clientHeight);
      const atBottom = distanceFromBottom <= 8;
      setAutoScrollEnabled((prev) => (prev === atBottom ? prev : atBottom));
    },
    [],
  );

  useEffect(() => {
    previousLatestTurnIDRef.current = undefined;
    setAutoScrollEnabled(true);
  }, [taskID]);

  useEffect(() => {
    const previous = previousLatestTurnIDRef.current;
    if (previous !== newestTurnID) {
      previousLatestTurnIDRef.current = newestTurnID;
      if (newestTurnID) {
        setAutoScrollEnabled(true);
        scrollToBottom();
      }
    }
  }, [newestTurnID, scrollToBottom]);

  useEffect(() => {
    if (!autoScrollEnabled) {
      return;
    }
    scrollToBottom();
  }, [
    autoScrollEnabled,
    scrollToBottom,
    newestTurnID,
    newestTurnStatus,
    newestTurnOutputMessage,
    newestTurnErrorMessage,
    orderedTurns.length,
  ]);
  useEffect(() => {
    if (!taskID || !latestTurnID) return;
    let cancelled = false;
    async function poll() {
      try {
          const resp = await fetch(
            `/api/tasks/${taskID}/turns/${latestTurnID}${wsQuery}`,
          );
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) {
            setTurns((prev) =>
              prev.map((p) => (p.TurnID === data.TurnID ? data : p)),
            );
            if (data.Status === "Done" || data.Status === "Failed") {
              cancelled = true;
            }
          }
        }
      } catch (err) {
        console.error("GetTurn failed", err);
      }
    }
    poll();
    const id = window.setInterval(() => {
      if (!cancelled) poll();
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [taskID, latestTurnID, wsQuery]);

  useEffect(() => {
    autoSelectionTargetRef.current = undefined;
    initialSelectionMadeRef.current = false;
  }, [taskID]);

  useEffect(() => {
    if (!onSelectTurn) {
      autoSelectionTargetRef.current = selectedTurnID;
      return;
    }

    if (!taskID) {
      autoSelectionTargetRef.current = undefined;
      initialSelectionMadeRef.current = false;
      return;
    }

    if (turns.length === 0) {
      autoSelectionTargetRef.current = undefined;
      initialSelectionMadeRef.current = false;
      if (selectedTurnID !== undefined) {
        onSelectTurn(undefined, { userInitiated: false });
      }
      return;
    }

    const pickNewestTurn = (predicate: (turn: Turn) => boolean) => {
      let newest: Turn | undefined;
      let newestValue = Number.NEGATIVE_INFINITY;
      let newestId = "";
      for (const turn of turns) {
        if (!turn?.TurnID) {
          continue;
        }
        if (!predicate(turn)) {
          continue;
        }
        const value = parseTurnNumber(turn.TurnID);
        if (
          !newest ||
          value > newestValue ||
          (value === newestValue && turn.TurnID > newestId)
        ) {
          newest = turn;
          newestValue = value;
          newestId = turn.TurnID;
        }
      }
      return newest;
    };

    const activeTurn = pickNewestTurn(
      (turn) => !TERMINAL_STATUSES.has(turn.Status),
    );

    if (activeTurn?.TurnID) {
      initialSelectionMadeRef.current = true;
      const previousAuto = autoSelectionTargetRef.current;
      autoSelectionTargetRef.current = activeTurn.TurnID;
      if (
        previousAuto !== activeTurn.TurnID &&
        selectedTurnID !== activeTurn.TurnID
      ) {
        onSelectTurn(activeTurn.TurnID, { userInitiated: false });
      } else if (selectedTurnID === undefined) {
        onSelectTurn(activeTurn.TurnID, { userInitiated: false });
      }
      return;
    }

    const latestTurn = pickNewestTurn(() => true);
    const latestTurnID = latestTurn?.TurnID;

    if (!latestTurnID) {
      autoSelectionTargetRef.current = undefined;
      if (selectedTurnID !== undefined) {
        onSelectTurn(undefined, { userInitiated: false });
      }
      return;
    }

    if (!initialSelectionMadeRef.current) {
      initialSelectionMadeRef.current = true;
      autoSelectionTargetRef.current = latestTurnID;
      if (selectedTurnID !== latestTurnID) {
        onSelectTurn(latestTurnID, { userInitiated: false });
      }
      return;
    }

    if (
      autoSelectionTargetRef.current &&
      !turns.some((turn) => turn.TurnID === autoSelectionTargetRef.current)
    ) {
      autoSelectionTargetRef.current = latestTurnID;
    }
  }, [turns, selectedTurnID, onSelectTurn, taskID]);

  useEffect(() => {
    return () => {
      if (copyTimeout.current !== null) {
        clearTimeout(copyTimeout.current);
      }
    };
  }, []);

  function handleCopy(prompt: string, id: string) {
    navigator.clipboard.writeText(stripExtendedContextMarker(prompt));
    setCopiedTurn(id);
    setPopupVisible(true);
    if (copyTimeout.current !== null) {
      clearTimeout(copyTimeout.current);
    }
    copyTimeout.current = window.setTimeout(() => {
      setPopupVisible(false);
      copyTimeout.current = window.setTimeout(() => {
        setCopiedTurn(null);
      }, 300);
    }, 1000);
  }

  const handleTurnClick = useCallback(
    (turnID?: string) => {
      if (!turnID || !onSelectTurn) {
        return;
      }
      initialSelectionMadeRef.current = true;
      onSelectTurn(turnID, { userInitiated: true });
    },
    [onSelectTurn],
  );

  const handleTurnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, turnID?: string) => {
      if (!turnID) {
        return;
      }
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "Spacebar"
      ) {
        event.preventDefault();
        handleTurnClick(turnID);
      }
    },
    [handleTurnClick],
  );

  const latestTurnStatus = newestTurnStatus;
  const effectiveTaskStatus = taskStatus ?? taskDetails?.Status;
  const isTaskTerminal = effectiveTaskStatus
    ? TERMINAL_STATUSES.has(effectiveTaskStatus)
    : true;
  const isTurnTerminal = latestTurnStatus
    ? TERMINAL_STATUSES.has(latestTurnStatus)
    : true;
  const showControls = Boolean(taskDetails) && isTaskTerminal && isTurnTerminal && ! taskDetails?.Deleted;
  const canCancel = Boolean(taskID && newestTurnID) && !showControls && !isTurnTerminal && !canceling;
  const trimmedPrompt = newPrompt.trim();
  const canSubmit = showControls && !submitting && trimmedPrompt.length > 0;

  async function handleCancelTurn() {
	if (!taskID || !newestTurnID) {
		setSubmitError("No active turn is available to cancel.");
		return;
	}

	setCanceling(true);
	setSubmitError(null);
	try {
		const resp = await fetch(`/api/tasks/${taskID}/turns/${newestTurnID}${wsQuery}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				Status: "Failed",
				ErrorMessage: "The user canceled the turn",
			}),
		});

		if (!resp.ok) {
			const { message } = await parseErrorResponse(resp);
			setSubmitError(message ? `Cancel turn failed: ${message}` : "Cancel turn failed.");
			return;
		}

		const updatedTurn: Turn = await resp.json();
		setTurns((prev) => prev.map((turn) => (turn.TurnID === updatedTurn.TurnID ? updatedTurn : turn)));

		await reloadTurns();
		try {
			const refreshResp = await fetch(`/api/tasks/${taskID}${wsQuery}`);
			if (refreshResp.ok) {
				const updated: Task = await refreshResp.json();
				setTaskDetails(updated);
			}
		} catch (refreshErr) {
			console.error("Failed to refresh task details after canceling turn", refreshErr);
		}
	} catch (err) {
		console.error("CancelTurn error", err);
		setSubmitError("Cancel turn failed due to a network error.");
	} finally {
		setCanceling(false);
	}
  }

  async function handleSubmitPrompt() {
    const prompt = newPrompt.trim();
    if (!prompt) {
      return;
    }
    if (!taskID) {
      setSubmitError("Task details are not available yet.");
      return;
    }

    let latestTaskDetails = taskDetails;
    try {
      const resp = await fetch(`/api/tasks/${taskID}${wsQuery}`);
      if (resp.ok) {
        const data: Task = await resp.json();
        latestTaskDetails = data;
        setTaskDetails(data);
      }
    } catch (err) {
      console.error("Failed to refresh task details", err);
    }

    if (!latestTaskDetails) {
      setSubmitError("Task details are not available yet.");
      return;
    }

    const taskVersion = latestTaskDetails.Version;
    if (taskVersion === undefined) {
      setSubmitError(
        "Task version is not available. Please reload the page and try again.",
      );
      return;
    }

    const knownTurnIDs: number[] = [];
    if (latestTaskDetails.TurnID) {
      const parsed = Number.parseInt(latestTaskDetails.TurnID, 10);
      if (Number.isFinite(parsed)) {
        knownTurnIDs.push(parsed);
      }
    }
    for (const turn of turns) {
      if (!turn?.TurnID) {
        continue;
      }
      const parsed = Number.parseInt(turn.TurnID, 10);
      if (Number.isFinite(parsed)) {
        knownTurnIDs.push(parsed);
      }
    }

    // For workstream tasks with no turns yet, start at turn 1.
    const nextTurnID = knownTurnIDs.length === 0
      ? "1"
      : String(Math.max(...knownTurnIDs) + 1);

    setSubmitting(true);
    setSubmitError(null);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "If-Match": String(taskVersion),
      };
      const resp = await fetch(`/api/tasks/${taskID}/turns/${nextTurnID}${wsQuery}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          Prompt: prompt,
          ...(newReasoningLevel && { ReasoningLevel: newReasoningLevel }),
        }),
      });
      if (resp.ok) {
        let createdTurn: { Version?: number } | null = null;
        try {
          createdTurn = await resp.json();
        } catch {
          createdTurn = null;
        }
        const newVersion = createdTurn?.Version;
        if (newVersion !== undefined) {
          setTaskDetails((prev) =>
            prev ? { ...prev, Version: newVersion } : prev,
          );
        }
        setNewPrompt("");
        // Don't reset reasoning level - it's persisted to localStorage and should remain for next turn
        if (taskID) {
          clearStoredTaskTurnDraft(taskID);
        }
        await reloadTurns();
        try {
          const refreshResp = await fetch(`/api/tasks/${taskID}${wsQuery}`);
          if (refreshResp.ok) {
            const updated: Task = await refreshResp.json();
            setTaskDetails(updated);
          }
        } catch (refreshErr) {
          console.error("Failed to refresh task details after creating turn", refreshErr);
        }
      } else {
        const { message, errorType } = await parseErrorResponse(resp);
        const runnerMessage = runnerErrorMessage(errorType);
        if (runnerMessage) {
          setSubmitError(runnerMessage);
          return;
        }
        if (resp.status === 409) {
          setSubmitError(
            "A turn is already in progress. Wait for it to finish before submitting another prompt.",
          );
          return;
        }
        const fallback = message
          ? `Create turn failed: ${message}`
          : "Create turn failed.";
        setSubmitError(fallback);
      }
    } catch (err) {
      console.error("CreateTurn error", err);
      setSubmitError("Create turn failed due to a network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        padding: "20px",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
        gap: "12px",
      }}
    >
	      <div
	        ref={scrollContainerRef}
	        onScroll={handleScroll}
	        style={{ flex: 1, overflowY: "auto", paddingRight: "4px", minHeight: 0 }}
	      >
	        {orderedTurns.map((t, idx) => {
	          const turnID = t.TurnID;
	          const isSelected =
	            selectedTurnID !== undefined && turnID === selectedTurnID;
	          const promptKey = String(turnID ?? idx);
	          const promptIsExpanded = expandedPrompts[promptKey] ?? false;
	          const statusLabel = formatTaskStatus(t.Status);
	          const showCancelForTurn = canCancel && turnID === newestTurnID;
	          return (
	            <div key={turnID || idx} style={{ marginBottom: "1em" }}>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => handleTurnClick(turnID)}
                onKeyDown={(event) => handleTurnKeyDown(event, turnID)}
                style={{
                  border: isSelected ? "1px solid #4b6cb7" : "1px solid transparent",
                  borderRadius: "8px",
                  padding: "8px",
                  background: isSelected ? "#eef3ff" : "transparent",
                  transition: "border 0.2s ease, background 0.2s ease",
                  cursor: turnID ? "pointer" : "default",
                }}
              >
                <PromptPreview
                  prompt={t.Prompt}
                  isExpanded={promptIsExpanded}
                  onToggle={() => togglePromptExpansion(promptKey)}
                >
                  {({ isOverflowing, isExpanded, toggle }) => (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "4px",
                        position: "relative",
                        gap: "0.5em",
                        flexWrap: "wrap",
                      }}
                    >
                      {isOverflowing && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle();
                          }}
                          className={`log-view-action-button${isExpanded ? " is-active" : ""}`}
                        >
                          {isExpanded ? "Show less" : "Show more"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCopy(t.Prompt || "", String(t.TurnID || idx));
                        }}
                        className="log-view-action-button with-icon"
                      >
                        Copy
                        {copiedTurn === String(t.TurnID || idx) ? (
                          <CheckIcon color="green" size="1em" />
                        ) : (
                          <CopyIcon />
                        )}
                      </button>
                      {copiedTurn === String(t.TurnID || idx) && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            right: 0,
                            background: "#333",
                            color: "#fff",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "0.75em",
                            opacity: popupVisible ? 1 : 0,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          Copied!
                        </div>
                      )}
                    </div>
                  )}
                </PromptPreview>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25em",
                    marginTop: "4px",
                  color: isSelected ? "#1d4ed8" : "inherit",
                  fontWeight: isSelected ? "bold" : "normal",
                }}
              >
                  Status: {statusLabel} <ChevronIcon size="1em" />
                </div>
                {t.Status === "Failed" && t.ErrorMessage && (
                  <div
                    style={{
                      background: "#2b2b2b",
                      border: "1px solid red",
                      borderRadius: "4px",
                      color: "red",
                      padding: "8px",
                      marginTop: "4px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {t.ErrorMessage}
                  </div>
                )}
	                {t.Status === "Done" && t.OutputMessage && (
	                  <div
	                    style={{
                      background: "#2b2b2b",
                      color: "#d4d4d4",
                      padding: "8px",
                      borderRadius: "4px",
                      marginTop: "4px",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      fontFamily: "serif",
                    }}
	                  >
	                    <ReactMarkdown>{t.OutputMessage}</ReactMarkdown>
	                  </div>
	                )}
	                {showCancelForTurn && (
	                  <div
	                    style={{
	                      display: "flex",
	                      justifyContent: "flex-end",
	                      marginTop: "8px",
	                    }}
	                  >
	                    <button
	                      type="button"
	                      onClick={() => {
	                        void handleCancelTurn();
	                      }}
	                      disabled={canceling}
	                      style={{ padding: "6px 12px" }}
	                    >
	                      {canceling ? "Canceling..." : "Cancel"}
	                    </button>
	                  </div>
	                )}
	              </div>
	              {idx < orderedTurns.length - 1 && <hr style={{ margin: "1em 0" }} />}
	            </div>
	          );
        })}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            paddingBottom: "100px",
          }}
        >
          {loadError && (
            <div style={{ color: "#b34700", fontSize: "0.9em" }}>{loadError}</div>
          )}
          {submitError && (
            <div style={{ color: "#b22222", fontSize: "0.9em" }}>{submitError}</div>
          )}
          {showControls && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <textarea
                value={newPrompt}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewPrompt(value);
                  if (taskID) {
                    if (value) {
                      saveStoredTaskTurnDraft(taskID, value);
                    } else {
                      clearStoredTaskTurnDraft(taskID);
                    }
                  }
                }}
                placeholder="Request changes"
                aria-label="Request changes"
                style={{
                  width: "100%",
                  minHeight: "100px",
                  resize: "vertical",
                  padding: "8px",
                  boxSizing: "border-box",
                }}
                disabled={submitting}
              />
            </div>
            {features['NewAgent'] && (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: "4px", flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: "0.85em", color: "#666", flexShrink: 0 }}>Model:</span>
                  <select
                    value={taskDetails?.ModelName || ''}
                    disabled
                    style={{
                      padding: "4px 8px",
                      opacity: 0.7,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <option value={taskDetails?.ModelName || ''}>
                      {taskDetails?.ModelName || 'Unknown'}
                    </option>
                  </select>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                  <span style={{ fontSize: "0.85em", color: "#666" }}>Reasoning:</span>
                  <select
                    value={newReasoningLevel}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewReasoningLevel(value);
                      if (taskID) {
                        saveStoredTaskReasoningLevel(taskID, value);
                      }
                    }}
                    disabled={submitting}
                    style={{ padding: "4px 8px" }}
                  >
                    {REASONING_LEVELS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleSubmitPrompt}
                disabled={!canSubmit}
                style={{ padding: "6px 12px" }}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
